import {useMemo} from 'react';
import type {List} from 'immutable';

import {
  getClassroomQuery,
  getCoteachersQuery,
  getInvitationsQuery,
  getStudentsQuery
} from 'client/Classrooms/Classroom/Teacher/People/people.queries';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';

import {useStoreChangePump} from '../useStoreChangePump';

// The roster renders client-side — in-memory search + single-column sort (last sort wins, matching
// the legacy MandarkTable) — rather than via MandarkTable, which is being retired
// A client-side list needs the whole roster in one fetch,
// so this caps the page size high enough to cover any realistic class.
const ROSTER_PAGE_SIZE = 1000;

// Minimal legacy-Mandark model shapes — only the getters the People views read. Mirrors the
// `as unknown as` pattern in useClassSettings rather than pulling full @albert-io/models types.
interface EmailDomain {
  getName: () => string;
}

export interface RosterSchool {
  getId: () => string;
  getName: () => string;
  isHardCap: () => boolean;
  getEmailDomains: () => List<EmailDomain> | undefined;
}

export interface RosterClassroom {
  getId: () => string;
  getName: () => string;
  isLicensed: () => boolean;
  getSchool: () => RosterSchool;
  getMeta: () => {getCountOfSchoolLicenseSeatsRemaining: () => number};
}

export interface RosterStudent {
  getId: () => string;
  getFirstName: () => string;
  getLastName: () => string;
  getEmail: () => string;
  getMeta: () => {isManagedUser: () => boolean};
}

export interface RosterInvitation {
  getId: () => string;
  getEmail: () => string;
  getSentEmailAt: () => {format: (token: string) => string} | null;
  getUpdatedAt: () => {format: (token: string) => string} | null;
}

export interface RosterTeacher {
  getId: () => string;
  getDisplayName: () => string;
  getEmail: () => string;
}

/** The legacy ManageRosterContext value the reused roster modals/dropdowns read. */
export interface ManageRosterContextValue {
  queries: {classroom: unknown; invitations: unknown; students: unknown};
  classroom: RosterClassroom | null;
  invitations: List<RosterInvitation>;
  students: List<RosterStudent>;
  isReady: boolean;
  modalToShow: string;
  setIsReady: (value: boolean) => void;
  setModalToShow: (value: string) => void;
}

/** The legacy PeopleContext value AddCoteacher / RemoveCoteacher read. */
export interface PeopleContextValue {
  classroom: RosterClassroom | null;
  queries: {coteachers: unknown};
  currentTeacherId: string;
}

export interface PeopleRoster {
  isReady: boolean;
  hasError: boolean;
  classroom: RosterClassroom | null;
  students: List<RosterStudent>;
  invitations: List<RosterInvitation>;
  coteachers: List<RosterTeacher>;
  currentTeacherId: string;
  reload: () => void;
  /** Same query instances the reused modals/dropdowns invalidate, so their mutations refresh this view. */
  queries: {classroom: unknown; invitations: unknown; students: unknown; coteachers: unknown};
}

/**
 * Loads the per-class roster (students, pending invitations, co-teachers + the classroom's license
 * seat meta) from the legacy Mandark JSON:API —
 */
export const usePeopleRoster = (classroomId: string): PeopleRoster => {
  const currentTeacherId = masqueradeStore.getUserIdByMasqueradeState() ?? '';

  const queries = useMemo(
    () => ({
      classroom: getClassroomQuery(classroomId),
      invitations: getInvitationsQuery(classroomId).pageSize(ROSTER_PAGE_SIZE),
      students: getStudentsQuery(classroomId).pageSize(ROSTER_PAGE_SIZE),
      coteachers: getCoteachersQuery(classroomId).pageSize(ROSTER_PAGE_SIZE)
    }),
    [classroomId]
  );

  const {isReady, invalidateQueries, errors} = useAreLatestQueriesReady([
    queries.classroom,
    queries.invitations,
    queries.students,
    queries.coteachers
  ]);

  // Passive Mandark reads don't subscribe React; re-render on store 'change' so a reused-flow
  // mutation (remove student, resend, add co-teacher…) reflects here.
  useStoreChangePump();

  const classroom = isReady ? (queries.classroom.getResource() as RosterClassroom) : null;
  const students = queries.students.getResource() as List<RosterStudent>;
  const invitations = queries.invitations.getResource() as List<RosterInvitation>;
  const coteachers = queries.coteachers.getResource() as List<RosterTeacher>;

  return {
    isReady,
    hasError: errors.some(Boolean),
    classroom,
    students,
    invitations,
    coteachers,
    currentTeacherId,
    reload: invalidateQueries,
    queries
  };
};
